import logo from './logo.svg';
import './App.css';

// Import fonts
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

// MUI theme configuration
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Theme from './Theme';

// Auth related imports
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { AuthProvider } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Error handling and popups
import { NotificationProvider } from './context/NotificationContext';
import NotificationPopup from './components/NotificationPopup';

import RootLayout from './components/RootLayout';

// Top level pages
import Login from './components/Auth/Login';
import Callback from './components/Auth/Callback';
import SelectTenant from './components/Auth/SelectTenant';
import Header from './components/Header';

// // Sales pages
// import SalesOrdersList from './components/Sales/Orders/SalesOrdersList';
// import SalesOrderDetails from './components/Sales/Orders/SalesOrderDetails';

// POS pages
import POS from './components/Sales/POS/POS';
import POSInitializeOrder from './components/Sales/POS/POSInitializeOrder';

// Invoices
import InvoicesList from './components/Sales/Invoices/InvoicesList';
import InvoiceDetails from './components/Sales/Invoices/InvoiceDetails';

// Customer pages
import CustomersList from './components/Customer/CustomersList';
import CustomerDetails from './components/Customer/CustomerDetails';
import CustomerForm from './components/Customer/CustomerForm';

// Employee pages
import EmployeesList from './components/Employee/EmployeesList';
import EmployeeDetails from './components/Employee/EmployeeDetails';
import EmployeeForm from './components/Employee/EmployeeForm';

// Vehicle pages
import VehiclesList from './components/Vehicle/VehiclesList';
import VehicleForm from './components/Vehicle/VehicleForm';
import VehicleDetails from './components/Vehicle/VehicleDetails';

// Item pages
import ItemsList from './components/Item/ItemsList';
import ItemDetails from './components/Item/ItemDetails';
import ProductForm from './components/Item/ProductForm';
import ServiceForm from './components/Item/ServiceForm';

import ItemCategoriesList from './components/Item/ItemCategoriesList';
import ItemCategoryDetails from './components/Item/ItemCategoryDetails';
import ItemCategoryForm from './components/Item/ItemCategoryForm';

// Location pages
import LocationList from './components/Location/LocationList';
import LocationDetails from './components/Location/LocationDetails';
import LocationForm from './components/Location/LocationForm';

// Inventory pages
import InventoryStockList from './components/Inventory/Stock/InventoryStockList';
import InventoryStockForm from './components/Inventory/Stock/InventoryStockForm';
import InventoryTransactionsList from './components/Inventory/Transactions/InventoryTransactionsList';

// Payment pages
import PaymentsList from './components/Payments/PaymentsList';
import AddPaymentForm from './components/Payments/AddPaymentForm';
import PaymentDetails from './components/Payments/PaymentDetails';

// Print templates
import PrintInvoice from './components/Sales/Invoices/PrintInvoice';

// Dashboard
import Dashboard from './components/Dashboard/Dashboard';

// Job Cards
import JobCardsList from './components/JobCards/JobCardsList';
import JobCardDetails from './components/JobCards/JobCardDetails';

// Work Orders
import WorkOrdersList from './components/WorkOrders/WorkOrdersList';
import WorkOrderDetails from './components/WorkOrders/WorkOrderDetails';

import Error404 from './components/Error404';


const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/auth/callback',
    element: <Callback />,
  },
  {
    path: '/select-tenant',
    element: (
      <ProtectedRoute requireTenant={false}>
        <>
          <Header />
          <SelectTenant />
        </>
      </ProtectedRoute>
    ),
  },
  { path: '/print/invoices', element: <PrintInvoice /> },
  {
    path: '/',
    element: <ProtectedRoute requireTenant={true}> <RootLayout /> </ProtectedRoute>,
    children: [

      // Dashboard - default page
      { index: true, element: <Dashboard /> },

      // POS routes
      { path: '/sales/pos/start', element: <POSInitializeOrder /> },
      { path: '/sales/pos/:workOrderId', element: <POS /> },

      // Job Cards routes (read-only)
      { path: '/sales/job-cards', element: <JobCardsList /> },
      { path: '/sales/job-cards/:id', element: <JobCardDetails /> },

      // Work Orders routes (read-only - created via POS or Job Cards)
      { path: '/sales/work-orders', element: <WorkOrdersList /> },
      { path: '/sales/work-orders/:id', element: <WorkOrderDetails /> },

      // Invoices routes
      { path: '/sales/invoices', element: <InvoicesList /> },
      { path: '/sales/invoices/:id', element: <InvoiceDetails /> },

      // { path: '/sales/orders', element: <SalesOrdersList /> },
      // { path: '/sales/orders/:id', element: <SalesOrderDetails /> },

      // Customer routes
      { path: '/customers', element: <CustomersList /> },
      { path: '/customers/add', element: <CustomerForm /> },
      { path: '/customers/add', element: <CustomerForm /> },
      { path: '/customers/:id', element: <CustomerDetails /> },
      { path: '/customers/:id/edit', element: <CustomerForm editMode={true} /> },

      // Employee routes
      { path: '/employees', element: <EmployeesList /> },
      { path: '/employees/add', element: <EmployeeForm /> },
      { path: '/employees/:id', element: <EmployeeDetails /> },
      { path: '/employees/:id/edit', element: <EmployeeForm editMode={true} /> },

      // Vehicle routes
      { path: '/vehicles', element: <VehiclesList /> },
      { path: '/vehicles/add', element: <VehicleForm /> },
      { path: '/vehicles/:id', element: <VehicleDetails /> },
      { path: '/vehicles/:id/edit', element: <VehicleForm editMode={true} /> },

      // Product groups routes
      { path: '/items/categories/products', element: <ItemCategoriesList type="PRODUCT" /> },
      { path: '/items/categories/products/add', element: <ItemCategoryForm type="PRODUCT" /> },
      { path: '/items/categories/products/:id', element: <ItemCategoryDetails /> },
      { path: '/items/categories/products/:id/edit', element: <ItemCategoryForm editMode={true} type="PRODUCT" /> },

      // Service groups routes
      { path: '/items/categories/services', element: <ItemCategoriesList type="SERVICE" /> },
      { path: '/items/categories/services/add', element: <ItemCategoryForm type="SERVICE" /> },
      { path: '/items/categories/services/:id', element: <ItemCategoryDetails /> },
      { path: '/items/categories/services/:id/edit', element: <ItemCategoryForm editMode={true} type="SERVICE" /> },

      // Product routes
      { path: '/items/products', element: <ItemsList type="PRODUCT" /> },
      { path: '/items/products/add', element: <ProductForm /> },
      { path: '/items/products/:id', element: <ItemDetails /> },
      { path: '/items/products/:id/edit', element: <ProductForm editMode={true} /> },

      // Service routes
      { path: '/items/services', element: <ItemsList type="SERVICE" /> },
      { path: '/items/services/add', element: <ServiceForm /> },
      { path: '/items/services/:id', element: <ItemDetails /> },
      { path: '/items/services/:id/edit', element: <ServiceForm editMode={true} /> },


      // Location routes
      { path: '/locations', element: <LocationList /> },
      { path: '/locations/add', element: <LocationForm /> },
      { path: '/locations/:id', element: <LocationDetails /> },
      { path: '/locations/:id/edit', element: <LocationForm editMode={true} /> },

      // Inventory routes
      { path: '/inventory/stocks', element: <InventoryStockList /> },
      { path: '/inventory/stocks/add', element: <InventoryStockForm /> },
      { path: '/inventory/transactions', element: <InventoryTransactionsList /> },

      // Payment routes
      { path: '/payments', element: <PaymentsList /> },
      { path: '/payments/add', element: <AddPaymentForm /> },
      { path: '/payments/:id', element: <PaymentDetails /> },



      // { path: '/sales', element: <SalesInsightsPage /> },
      { path: '/*', element: <Error404 /> }, // 404 Page
    ]
  }
]);

function App() {
  return (
    <ThemeProvider theme={Theme}>
      <CssBaseline />
      <TenantProvider>
        <AuthProvider>
          <NotificationProvider>
            <NotificationPopup />
            <RouterProvider router={router} />
          </NotificationProvider>
        </AuthProvider>
      </TenantProvider>
    </ThemeProvider>
  );
}

export default App;
